import {
  Form,
  Input,
  Modal,
  Select,
  Table,
  message,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";

import PropTypes from "prop-types";
import RowActions, { ACTIONS_COLUMN } from "../utils/RowActions";
import SettingsHeader from "./SettingsHeader";
import axios from "../../utils/axios";

export const repositoryLabel = (r) =>
  `${r.name} - ${r.project} - ${r.url}${r.project_uri || ""}`;

/* depends_on peut arriver sous forme de tableau ou de litteral PG "{1,2}" */
export const parseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const inner = value.replace(/^[{[]|[}\]]$/g, "").trim();
    if (!inner) return [];
    return inner
      .split(",")
      .map((v) => Number(v.trim()))
      .filter((v) => !Number.isNaN(v));
  }
  return [];
};

export const errorMessage = (e) =>
  e?.response?.data?.error ||
  e?.response?.data?.message ||
  e?.message ||
  "Erreur inconnue";

/**
 * Vue CRUD generique : props.table = modules | repositories
 * columns : [{ key, title, type: "text"|"repository"|"json", required }]
 */
function ParamTable({ table, title, columns }) {
  const [rows, setRows] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (ajout) | row
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/${table}`);
      setRows(Array.isArray(res.data) ? res.data : []);
      if (columns.some((c) => c.type === "repository")) {
        const repos = await axios.get("/api/repositories");
        setRepositories(Array.isArray(repos.data) ? repos.data : []);
      }
    } catch (e) {
      message.error(errorMessage(e));
    }
    setLoading(false);
  }, [table, columns]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (row) => {
    const values = {};
    columns.forEach((c) => {
      const v = row ? row[c.key] : undefined;
      values[c.key] =
        c.type === "json"
          ? JSON.stringify(parseArray(v))
          : v === null
          ? undefined
          : v;
    });
    form.resetFields();
    form.setFieldsValue(values);
    setEditing(row || {});
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return; // erreurs de validation deja affichees par le formulaire
    }
    const payload = {};
    columns.forEach((c) => {
      let v = values[c.key];
      if (c.type === "json") {
        v = JSON.parse(v || "[]");
      } else if (v === undefined || v === "") {
        v = null;
      }
      payload[c.key] = v;
    });
    try {
      if (editing.oid) {
        await axios.put(`/api/${table}/${editing.oid}`, payload);
      } else {
        await axios.post(`/api/${table}`, payload);
      }
      message.success("Enregistre");
      setEditing(null);
      load();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const handleDelete = async (row) => {
    try {
      await axios.delete(`/api/${table}/${row.oid}`);
      message.success("Supprime");
      load();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const tableColumns = [
    { title: "oid", dataIndex: "oid", key: "oid", width: 70 },
    ...columns.map((c) => ({
      title: c.title || c.key,
      dataIndex: c.key,
      key: c.key,
      render: (v) => {
        if (c.type === "json") return JSON.stringify(parseArray(v));
        if (c.type === "repository") {
          const r = repositories.find((x) => x.oid === v);
          return r ? repositoryLabel(r) : v ?? "";
        }
        return v ?? "";
      },
    })),
    {
      ...ACTIONS_COLUMN,
      render: (text, row) => (
        <RowActions
          onEdit={() => openForm(row)}
          onDelete={() => handleDelete(row)}
        />
      ),
    },
  ];

  const renderInput = (c) => {
    if (c.type === "repository") {
      return (
        <Select
          allowClear
          showSearch
          placeholder="Aucun"
          optionFilterProp="label"
          options={repositories.map((r) => ({
            value: r.oid,
            label: repositoryLabel(r),
          }))}
        />
      );
    }
    if (c.type === "json") return <Input placeholder="[1, 2]" />;
    return <Input />;
  };

  return (
    <div>
      <SettingsHeader title={title} onAdd={() => openForm(null)} />
      <Table
        rowKey="oid"
        size="small"
        loading={loading}
        columns={tableColumns}
        dataSource={rows}
        pagination={false}
      />
      <Modal
        title={editing && editing.oid ? "Modifier" : "Ajouter"}
        open={!!editing}
        okText="Enregistrer"
        cancelText="Annuler"
        onOk={handleSubmit}
        onCancel={() => setEditing(null)}
        forceRender
      >
        <Form form={form} layout="vertical">
          {columns.map((c) => (
            <Form.Item
              key={c.key}
              name={c.key}
              label={c.title || c.key}
              rules={[
                { required: !!c.required, message: "Champ requis" },
                ...(c.type === "json"
                  ? [
                      {
                        validator: (_, v) => {
                          try {
                            if (!Array.isArray(JSON.parse(v || "[]")))
                              throw new Error();
                            return Promise.resolve();
                          } catch (e) {
                            return Promise.reject(
                              new Error("JSON invalide (tableau attendu)")
                            );
                          }
                        },
                      },
                    ]
                  : []),
              ]}
            >
              {renderInput(c)}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
}

ParamTable.propTypes = {
  table: PropTypes.string.isRequired,
  title: PropTypes.string,
  columns: PropTypes.array.isRequired,
};

export default ParamTable;
